import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const detectWebView = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera || '';
  const isAndroidWebView = /; wv\)/i.test(ua) || (ua.includes('Android') && ua.includes('Version/'));
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua);
  const isFlutterWebView = /flutter|wvapp/i.test(ua);
  return isAndroidWebView || isIOSWebView || isFlutterWebView;
};

const RegistrarVisitante = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastBitacoraId, setLastBitacoraId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [visitanteInfoModal, setVisitanteInfoModal] = useState({ nombre: '', apellido: '' });

  const [nombreCompleto, setNombreCompleto] = useState('');
  const [autorizadoPor, setAutorizadoPor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [firmaArchivo, setFirmaArchivo] = useState(null);
  const [firmaPreview, setFirmaPreview] = useState('');
  const [isFirmaModalOpen, setIsFirmaModalOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [firmaTieneTrazo, setFirmaTieneTrazo] = useState(false);
  const [requiereFirma, setRequiereFirma] = useState(true); // Por defecto requiere firma
  const canvasRef = useRef(null);
  
  // Estados para autocompletado del catálogo
  const [catalogoFuncionarios, setCatalogoFuncionarios] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const sugerenciasRef = useRef(null);
  const [funcionarioSeleccionado, setFuncionarioSeleccionado] = useState(null);
  const nombreCompletoOriginalRef = useRef('');
  
  // Estados para autocompletado del campo "Persona que autoriza"
  const [sugerenciasAutorizado, setSugerenciasAutorizado] = useState([]);
  const [mostrarSugerenciasAutorizado, setMostrarSugerenciasAutorizado] = useState(false);
  const sugerenciasAutorizadoRef = useRef(null);
  const capturaInputId = useMemo(() => `visitante-foto-captura-${uuidv4()}`, []);
  const isWebView = useMemo(() => {
    try {
      return detectWebView();
    } catch (error) {
      console.warn('No se pudo detectar WebView:', error);
      return false;
    }
  }, []);

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  // Cargar catálogo de funcionarios
  const fetchCatalogoFuncionarios = useCallback(async () => {
    if (!user?.id_generado) return;

    try {
      const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO'));
      url.searchParams.set('id_generado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Filtrar solo funcionarios
        const funcionarios = (data.data || []).filter(item => item.tipo === 'FUNCIONARIO');
        setCatalogoFuncionarios(funcionarios);
      }
    } catch (err) {
      console.error('Error cargando catálogo de funcionarios:', err);
    }
  }, [user?.id_generado]);

  // Cargar catálogo al montar el componente
  useEffect(() => {
    fetchCatalogoFuncionarios();
  }, [fetchCatalogoFuncionarios]);

  // Filtrar sugerencias mientras se escribe
  useEffect(() => {
    if (nombreCompleto.trim().length >= 2) {
      const textoBusqueda = nombreCompleto.trim().toLowerCase();
      const filtradas = catalogoFuncionarios.filter(funcionario => {
        const nombreCompletoFuncionario = funcionario.Dato?.toLowerCase() || '';
        return nombreCompletoFuncionario.includes(textoBusqueda);
      });
      
      // Verificar si el texto coincide exactamente con alguna sugerencia
      const coincideExactamente = filtradas.some(funcionario => 
        funcionario.Dato?.toLowerCase() === textoBusqueda
      );
      
      setSugerencias(filtradas);
      // No mostrar sugerencias si coincide exactamente o si no hay coincidencias
      setMostrarSugerencias(filtradas.length > 0 && !coincideExactamente);
    } else {
      setSugerencias([]);
      setMostrarSugerencias(false);
    }
  }, [nombreCompleto, catalogoFuncionarios]);

  // Filtrar sugerencias de "Persona que autoriza" mientras se escribe
  useEffect(() => {
    if (autorizadoPor.trim().length >= 2 && !funcionarioSeleccionado) {
      const textoBusqueda = autorizadoPor.trim().toLowerCase();
      const filtradas = catalogoFuncionarios.filter(funcionario => {
        const nombreCompletoFuncionario = funcionario.Dato?.toLowerCase() || '';
        return nombreCompletoFuncionario.includes(textoBusqueda);
      });
      
      // Verificar si el texto coincide exactamente con alguna sugerencia
      const coincideExactamente = filtradas.some(funcionario => 
        funcionario.Dato?.toLowerCase() === textoBusqueda
      );
      
      setSugerenciasAutorizado(filtradas);
      // No mostrar sugerencias si coincide exactamente o si no hay coincidencias
      setMostrarSugerenciasAutorizado(filtradas.length > 0 && !coincideExactamente);
    } else {
      setSugerenciasAutorizado([]);
      setMostrarSugerenciasAutorizado(false);
    }
  }, [autorizadoPor, catalogoFuncionarios, funcionarioSeleccionado]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sugerenciasRef.current && !sugerenciasRef.current.contains(event.target)) {
        setMostrarSugerencias(false);
      }
      if (sugerenciasAutorizadoRef.current && !sugerenciasAutorizadoRef.current.contains(event.target)) {
        setMostrarSugerenciasAutorizado(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSeleccionarSugerencia = (funcionario) => {
    const nombreSeleccionado = funcionario.Dato || '';
    setNombreCompleto(nombreSeleccionado);
    // Guardar referencia del nombre seleccionado
    nombreCompletoOriginalRef.current = nombreSeleccionado;
    setFuncionarioSeleccionado(funcionario);
    // Ocultar campo de autorizado y llenar observaciones
    setAutorizadoPor('');
    setObservaciones('Ingreso de funcionario');
    // Cerrar inmediatamente y limpiar sugerencias
    setMostrarSugerencias(false);
    setSugerencias([]);
    // Limpiar errores relacionados
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.autorizadoPor;
      return newErrors;
    });
    // Hacer focus en el input para que el usuario pueda continuar
    setTimeout(() => {
      const input = document.getElementById('visitante-nombre-completo');
      if (input) {
        input.focus();
        // Mover el cursor al final
        input.setSelectionRange(nombreSeleccionado.length, nombreSeleccionado.length);
      }
    }, 0);
  };

  const handleSeleccionarSugerenciaAutorizado = (funcionario) => {
    const nombreSeleccionado = funcionario.Dato || '';
    setAutorizadoPor(nombreSeleccionado);
    // Cerrar inmediatamente y limpiar sugerencias
    setMostrarSugerenciasAutorizado(false);
    setSugerenciasAutorizado([]);
    // Limpiar errores relacionados
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.autorizadoPor;
      return newErrors;
    });
    // Hacer focus en el input para que el usuario pueda continuar
    setTimeout(() => {
      const input = document.getElementById('visitante-autorizado');
      if (input) {
        input.focus();
        // Mover el cursor al final
        input.setSelectionRange(nombreSeleccionado.length, nombreSeleccionado.length);
      }
    }, 0);
  };

  const resetForm = useCallback(() => {
    setNombreCompleto('');
    setSugerencias([]);
    setMostrarSugerencias(false);
    setFuncionarioSeleccionado(null);
    nombreCompletoOriginalRef.current = '';
    setAutorizadoPor('');
    setObservaciones('');
    setFotoArchivo(null);
    setFotoPreview('');
    setFirmaArchivo(null);
    setFirmaPreview('');
    setIsFirmaModalOpen(false);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setFieldErrors({});
    setError('');
    // No limpiar success aquí, se cerrará con el modal
  }, []);

  // Obtener configuración de firma
  const fetchConfigFirma = useCallback(async () => {
    if (!user?.id_generado) return;

    try {
      const url = new URL(getApiUrl('BITACORA', 'CONFIG_FIRMA'));
      url.searchParams.set('idGenerado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRequiereFirma(data.data.requiereFirma);
      }
    } catch (err) {
      console.error('Error cargando configuración de firma:', err);
      // En caso de error, mantener el valor por defecto (true)
    }
  }, [user?.id_generado]);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) return;

      if (!hasPuestoAsignado) {
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        setStatus(null);
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');
      // No limpiar success si se indica preserveSuccess (cuando el modal está abierto)
      // En este caso no se pasa preserveSuccess, así que siempre limpia success
      setSuccess('');

      try {
        const url = new URL(getApiUrl('BITACORA', 'STATUS'));
        url.searchParams.set('idGenerado', user.id_generado);
        url.searchParams.set('username', user.username || '');
        url.searchParams.set('appid', user.appid || '');

        const response = await fetch(url.toString());
        const data = await response.json();

        if (response.ok && data.success) {
          setStatus(data.data);
          setLastBitacoraId(null);
        } else {
          setStatus(null);
          setError(data.error || 'No fue posible obtener el estado de la bitácora.');
        }
      } catch (err) {
        console.error('Error cargando estado de bitácora:', err);
        setStatus(null);
        setError('Error de conexión al cargar el estado de la bitácora.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user, hasPuestoAsignado]
  );

  useEffect(() => {
    fetchStatus();
    fetchConfigFirma();
  }, [fetchStatus, fetchConfigFirma]);

  // Debug: Verificar cuando cambia showSuccessModal
  useEffect(() => {
    if (showSuccessModal) {
      console.log('✅ Modal de éxito ACTIVADO:', { 
        showSuccessModal, 
        success, 
        lastBitacoraId,
        timestamp: new Date().toISOString()
      });
    } else {
      console.log('❌ Modal de éxito DESACTIVADO');
    }
  }, [showSuccessModal, success, lastBitacoraId]);

  useEffect(() => {
    if (!fotoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(fotoPreview);
    };
  }, [fotoPreview]);

  useEffect(() => {
    if (!firmaPreview) return undefined;
    return () => {
      URL.revokeObjectURL(firmaPreview);
    };
  }, [firmaPreview]);

  useEffect(() => {
    if (!isFirmaModalOpen) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
  }, [isFirmaModalOpen]);

  const optimizeImage = useCallback((file, { maxWidth = 1024, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      reader.onerror = reject;

      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo optimizar la imagen.'));
              return;
            }

            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const optimizedFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        setFotoArchivo(null);
        setFotoPreview('');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten imágenes para la identificación del visitante.');
        event.target.value = '';
        return;
      }

      let finalFile = file;
      const ONE_MB = 1024 * 1024;

      try {
        if (file.size > ONE_MB) {
          const optimized = await optimizeImage(file, { maxWidth: 1024, quality: 0.7 });
          console.log(`Original: ${(file.size / 1024).toFixed(1)} KB`);
          console.log(`Optimizada: ${(optimized.size / 1024).toFixed(1)} KB`);
          finalFile = optimized;
        } else {
          console.log(`Imagen menor a 1MB, se enviará sin cambios (${(file.size / 1024).toFixed(1)} KB).`);
        }
      } catch (optimizationError) {
        console.error('Error optimizando imagen:', optimizationError);
        setError('No se pudo optimizar la imagen. Intenta con otra foto.');
        event.target.value = '';
        return;
      }

      setFotoArchivo(finalFile);
      setFotoPreview(URL.createObjectURL(finalFile));
    },
    [optimizeImage, setError]
  );

  const handleOpenFirmaModal = useCallback(() => {
    if (submitting || status?.puedeBitacora !== 'SI') return;
    setError('');
    setIsFirmaModalOpen(true);
  }, [status?.puedeBitacora, submitting]);

  const handleCloseFirmaModal = useCallback(() => {
    setIsFirmaModalOpen(false);
    setIsDrawing(false);
    setFirmaTieneTrazo(false);
  }, []);

  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX ?? 0) - rect.left) * (canvas.width / rect.width);
    const y = ((event.clientY ?? 0) - rect.top) * (canvas.height / rect.height);
    return { x, y };
  }, []);

  const handleFirmaPointerDown = useCallback((event) => {
    if (!isFirmaModalOpen || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setFirmaTieneTrazo(true);
  }, [getCanvasCoordinates, isFirmaModalOpen]);

  const handleFirmaPointerMove = useCallback((event) => {
    if (!isDrawing || !canvasRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCanvasCoordinates, isDrawing]);

  const handleFirmaPointerUp = useCallback((event) => {
    if (!canvasRef.current) return;
    event?.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
  }, []);

  const handleClearFirma = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, []);

  const handleGuardarFirma = useCallback(() => {
    if (!canvasRef.current) return;
    if (!firmaTieneTrazo) {
      setError('Por favor registra la firma del visitante.');
      return;
    }
    setError('');
    canvasRef.current.toBlob((blob) => {
      if (!blob) {
        setError('No se pudo generar la firma. Intenta nuevamente.');
        return;
      }
      const file = new File([blob], `firma_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      if (firmaPreview) {
        URL.revokeObjectURL(firmaPreview);
      }
      const previewUrl = URL.createObjectURL(blob);
      setFirmaArchivo(file);
      setFirmaPreview(previewUrl);
      setIsFirmaModalOpen(false);
      
      // Limpiar error de firma si existe
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.firma;
        return newErrors;
      });
    }, 'image/jpeg', 0.9);
  }, [firmaPreview, firmaTieneTrazo]);

  const handleEliminarFirma = useCallback(() => {
    if (firmaPreview) {
      URL.revokeObjectURL(firmaPreview);
    }
    setFirmaArchivo(null);
    setFirmaPreview('');
    setFirmaTieneTrazo(false);
    setIsDrawing(false);
    setError('');
  }, [firmaPreview]);

  const handleCloseErrorModal = useCallback(() => {
    setError('');
  }, []);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
    setVisitanteInfoModal({ nombre: '', apellido: '' });
    // Actualizar el status después de cerrar el modal
    if (user && status) {
      fetchStatus(false).catch(err => {
        console.error('Error actualizando status:', err);
      });
    }
  }, [user, status, fetchStatus]);

  const scrollToField = (fieldId) => {
    const field = document.getElementById(fieldId);
    if (field) {
      // Para secciones, hacer scroll a la sección
      if (fieldId === 'foto-section' || fieldId === 'firma-section') {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Resaltar la sección por un momento
        field.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
        }, 2000);
      } else {
        // Para campos normales, hacer focus y scroll
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus();
        // Resaltar el campo por un momento
        field.classList.add('ring-2', 'ring-red-500');
        setTimeout(() => {
          field.classList.remove('ring-2', 'ring-red-500');
        }, 2000);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!status) return;

    if (status.puedeBitacora !== 'SI') {
      setError('La bitácora está deshabilitada. Primero debes relevar el puesto.');
      return;
    }

    // Separar nombre completo en nombre y apellido
    const nombreCompletoTrim = nombreCompleto.trim();
    const partesNombre = nombreCompletoTrim.split(/\s+/).filter(p => p.length > 0);
    const nombre = partesNombre.length > 0 ? partesNombre[0] : '';
    const apellido = partesNombre.length > 1 ? partesNombre.slice(1).join(' ') : '';

    // Validar todos los campos obligatorios
    const errors = {};
    let firstErrorField = null;

    if (!nombreCompletoTrim) {
      errors.nombreCompleto = 'El nombre y apellido son obligatorios';
      if (!firstErrorField) firstErrorField = 'visitante-nombre-completo';
    } else if (partesNombre.length < 2) {
      errors.nombreCompleto = 'Por favor ingresa nombre y apellido';
      if (!firstErrorField) firstErrorField = 'visitante-nombre-completo';
    }

    // Solo validar autorizadoPor si NO hay un funcionario seleccionado
    if (!funcionarioSeleccionado && !autorizadoPor.trim()) {
      errors.autorizadoPor = 'La persona que autoriza es obligatoria';
      if (!firstErrorField) firstErrorField = 'visitante-autorizado';
    }

    // Solo validar firma si es requerida
    if (requiereFirma && !firmaArchivo) {
      errors.firma = 'La firma es obligatoria';
      if (!firstErrorField) firstErrorField = 'firma-section';
    }

    // Solo validar observaciones si NO hay un funcionario seleccionado
    if (!funcionarioSeleccionado && (!observaciones || !observaciones.trim())) {
      errors.observaciones = 'Las observaciones son obligatorias';
      if (!firstErrorField) firstErrorField = 'visitante-observaciones';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      
      // Construir mensaje de error detallado
      const camposFaltantes = Object.keys(errors).map(key => {
      const nombres = {
        nombreCompleto: 'Nombre y apellido',
        autorizadoPor: 'Persona que autoriza',
        firma: 'Firma',
        observaciones: 'Observaciones'
      };
        return nombres[key];
      }).join(', ');
      
      setError(`Por favor completa los siguientes campos obligatorios: ${camposFaltantes}.`);
      
      // Hacer scroll al primer campo con error
      if (firstErrorField) {
        setTimeout(() => {
          scrollToField(firstErrorField);
        }, 100);
      }
      
      return;
    }

    // Limpiar errores si todo está bien
    setFieldErrors({});

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('idGenerado', status.idGenerado);
      formData.append('tipo', 'VISITANTE');
      formData.append('observaciones', observaciones);
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      formData.append('visitanteNombre', nombre.trim());
      formData.append('visitanteApellido', apellido.trim());
      // Si hay funcionario seleccionado, usar texto fijo, sino usar el campo
      formData.append('autorizadoPor', funcionarioSeleccionado ? 'Ingreso de funcionario' : autorizadoPor.trim());
      if (fotoArchivo) {
        formData.append('foto', fotoArchivo);
      }
      if (firmaArchivo) {
        formData.append('firma', firmaArchivo);
      }

      const response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const successMessage = data.message || 'VISITANTE guardado.';
        const bitacoraId = data.bitacoraId || null;
        
        // Guardar información del visitante antes de limpiar el formulario
        setVisitanteInfoModal({ nombre, apellido });
        
        // Establecer los valores del éxito y mostrar el modal
        setSuccess(successMessage);
        setLastBitacoraId(bitacoraId);
        setShowSuccessModal(true);
        
        console.log('Modal de éxito configurado:', { 
          showSuccessModal: true, 
          success: successMessage, 
          bitacoraId 
        });
        
        // Limpiar el formulario inmediatamente (no afecta el modal)
        resetForm();
        
        // Actualizar el status en segundo plano sin limpiar el success
        // fetchStatus se llamará después de cerrar el modal
      } else {
        setError(data.error || 'No fue posible registrar al visitante.');
      }
    } catch (err) {
      console.error('Error registrando visitante:', err);
      setError('Error de conexión al registrar al visitante.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWebViewCaptureTest = useCallback(() => {
    if (!isWebView) {
      console.info('El botón de prueba WebView se reservó para webviews.');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';

    const cleanup = () => {
      if (input.parentNode) {
        input.parentNode.removeChild(input);
      }
    };

    input.addEventListener('change', (event) => {
      if (event.target.files?.length) {
        handleFileChange(event);
      }
      cleanup();
    });

    input.addEventListener('cancel', cleanup);
    document.body.appendChild(input);
    input.click();
  }, [handleFileChange, isWebView]);

  const renderEventosRecientes = () => {
    if (!status?.eventosRecientes || status.eventosRecientes.length === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6">
          <p className="text-sm text-gray-600">No hay eventos recientes registrados.</p>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Últimos eventos</h2>
          <p className="text-sm text-gray-500">
            Mostrando los 10 registros más recientes del puesto.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Evento
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hora
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Observación
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {status.eventosRecientes.map((evento, index) => (
                <tr key={`${evento.tipo_evento}-${evento.hora_registro_servidor}-${index}`}>
                  <td className="px-4 py-3 text-sm font-medium text-indigo-600">{evento.tipo_evento}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {evento.hora_ingreso || new Date(evento.hora_registro_servidor).toLocaleTimeString('es-EC', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{evento.observacion || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Registrar Ingreso</h1>
            <p className="text-gray-600">
              Registra el ingreso de visitantes y funcionarios en bitácora.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchStatus(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={loading || refreshing || submitting}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Actualizando...</span>
              </span>
            ) : (
              'Actualizar estado'
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando información…</span>
            </div>
          </div>
        ) : (
          <>

            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-6">
              <div className="relative" ref={sugerenciasRef}>
                <label htmlFor="visitante-nombre-completo" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre y apellido <span className="text-red-500">*</span>
                </label>
                <input
                  id="visitante-nombre-completo"
                  type="text"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.nombreCompleto ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ingresa el nombre y apellido"
                  value={nombreCompleto}
                  onChange={(e) => {
                    const nuevoValor = e.target.value;
                    setNombreCompleto(nuevoValor);
                    
                    // Si había un funcionario seleccionado y se modifica el texto
                    if (funcionarioSeleccionado && nuevoValor !== nombreCompletoOriginalRef.current) {
                      // Restaurar campo de autorizado y limpiar observaciones
                      setFuncionarioSeleccionado(null);
                      nombreCompletoOriginalRef.current = '';
                      setObservaciones('');
                      // El campo de autorizado se mostrará automáticamente
                    }
                    
                    if (fieldErrors.nombreCompleto) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.nombreCompleto;
                        return newErrors;
                      });
                    }
                  }}
                  onFocus={() => {
                    if (nombreCompleto.trim().length >= 2 && sugerencias.length > 0) {
                      setMostrarSugerencias(true);
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                  required
                />
                {fieldErrors.nombreCompleto && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.nombreCompleto}
                  </p>
                )}
                
                {/* Dropdown de sugerencias */}
                {mostrarSugerencias && sugerencias.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {sugerencias.map((funcionario) => (
                      <button
                        key={funcionario.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none transition-colors"
                        onClick={() => handleSeleccionarSugerencia(funcionario)}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-900">{funcionario.Dato}</span>
                          {funcionario.Descripcion && (
                            <span className="text-xs text-gray-500 ml-auto">({funcionario.Descripcion})</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div id="foto-section">
              <label htmlFor="visitante-foto" className="block text-sm font-medium text-gray-700 mb-1">
                Identificación del visitante (Foto opcional)
              </label>
              <div className={`space-y-2 ${fieldErrors.foto ? 'p-3 border border-red-500 rounded-lg bg-red-50' : ''}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    id={capturaInputId + '-button'}
                    onClick={() => {
                      document.getElementById(capturaInputId)?.click();
                      if (fieldErrors.foto) {
                        setFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.foto;
                          return newErrors;
                        });
                      }
                    }}
                    className="inline-flex justify-center items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                  >
                    Capturar foto
                  </button>
                  {isWebView && (
                    <button
                      type="button"
                      onClick={handleWebViewCaptureTest}
                      className="inline-flex justify-center items-center px-4 py-2 border border-indigo-200 text-sm font-medium rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                      disabled={submitting || status?.puedeBitacora !== 'SI'}
                    >
                      Prueba cámara WebView
                    </button>
                  )}
                </div>

                <input
                  id={capturaInputId}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e);
                    if (fieldErrors.foto) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.foto;
                        return newErrors;
                      });
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                />
                {fieldErrors.foto && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.foto}
                  </p>
                )}
              </div>
              {fotoPreview && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-1">Previsualización:</p>
                  <img
                    src={fotoPreview}
                    alt="Identificación del visitante"
                    className="w-full max-w-xs rounded-lg border border-gray-200"
                  />
                </div>
              )}
            </div>

            <div id="firma-section">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Firma del visitante {requiereFirma ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-gray-500 text-xs font-normal">(Opcional)</span>
                )}
              </label>
              <div className={`space-y-3 ${fieldErrors.firma ? 'p-3 border border-red-500 rounded-lg bg-red-50' : ''}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      handleOpenFirmaModal();
                      if (fieldErrors.firma) {
                        setFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.firma;
                          return newErrors;
                        });
                      }
                    }}
                    className="inline-flex justify-center items-center px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-60"
                    disabled={submitting || status?.puedeBitacora !== 'SI'}
                  >
                    Capturar firma
                  </button>
                  {firmaArchivo && (
                    <button
                      type="button"
                      onClick={handleEliminarFirma}
                      className="inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      disabled={submitting}
                    >
                      Eliminar firma
                    </button>
                  )}
                </div>

                {fieldErrors.firma && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.firma}
                  </p>
                )}

                {firmaPreview ? (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Firma registrada:</p>
                    <img
                      src={firmaPreview}
                      alt="Firma del visitante"
                      className="w-full max-w-xs rounded-lg border border-gray-200 bg-white"
                    />
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Solicita al visitante que firme. Esta firma se guardará junto con el registro.</p>
                )}
              </div>
            </div>

            {!funcionarioSeleccionado && (
              <div className="relative" ref={sugerenciasAutorizadoRef}>
                <label htmlFor="visitante-autorizado" className="block text-sm font-medium text-gray-700 mb-1">
                  Persona que autoriza el ingreso <span className="text-red-500">*</span>
                </label>
                <input
                  id="visitante-autorizado"
                  type="text"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.autorizadoPor ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ingresa quien autoriza"
                  value={autorizadoPor}
                  onChange={(e) => {
                    setAutorizadoPor(e.target.value);
                    if (fieldErrors.autorizadoPor) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.autorizadoPor;
                        return newErrors;
                      });
                    }
                  }}
                  onFocus={() => {
                    if (autorizadoPor.trim().length >= 2 && sugerenciasAutorizado.length > 0) {
                      setMostrarSugerenciasAutorizado(true);
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                  required
                />
                {fieldErrors.autorizadoPor && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.autorizadoPor}
                  </p>
                )}
                
                {/* Dropdown de sugerencias para "Persona que autoriza" */}
                {mostrarSugerenciasAutorizado && sugerenciasAutorizado.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {sugerenciasAutorizado.map((funcionario) => (
                      <button
                        key={funcionario.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none transition-colors"
                        onClick={() => handleSeleccionarSugerenciaAutorizado(funcionario)}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-900">{funcionario.Dato}</span>
                          {funcionario.Descripcion && (
                            <span className="text-xs text-gray-500 ml-auto">({funcionario.Descripcion})</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

              <div>
                <label htmlFor="visitante-observaciones" className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones {funcionarioSeleccionado ? (
                    <span className="text-gray-500 text-xs font-normal">(opcional)</span>
                  ) : (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  id="visitante-observaciones"
                  rows={3}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.observaciones ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ejemplos: Ingresa con computadora, Pasa al departamento de contabilidad, Proveedor de trabajos, etc."
                  value={observaciones}
                  onChange={(e) => {
                    setObservaciones(e.target.value);
                    if (fieldErrors.observaciones) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.observaciones;
                        return newErrors;
                      });
                    }
                  }}
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                  required={!funcionarioSeleccionado}
                />
                {fieldErrors.observaciones && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.observaciones}
                  </p>
                )}
              </div>

              {status?.puedeBitacora !== 'SI' && (
                <p className="text-xs text-red-600 font-medium">
                  Bitácora deshabilitada, primero debe relevar el puesto.
                </p>
              )}

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Guardando...</span>
                    </span>
                  ) : (
                    'Registrar Ingreso'
                  )}
                </button>
              </div>
            </form>

            {isFirmaModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Firma del visitante</h3>
                      <p className="text-sm text-gray-500">Pide al visitante que firme dentro del recuadro.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseFirmaModal}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                      aria-label="Cerrar"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="border border-dashed border-gray-300 rounded-xl bg-gray-100 p-3">
                      <canvas
                        ref={canvasRef}
                        width={800}
                        height={320}
                        className="w-full h-48 sm:h-64 bg-white border border-gray-300 rounded-lg shadow-inner"
                        style={{ touchAction: 'none' }}
                        onPointerDown={handleFirmaPointerDown}
                        onPointerMove={handleFirmaPointerMove}
                        onPointerUp={handleFirmaPointerUp}
                        onPointerLeave={handleFirmaPointerUp}
                        onPointerCancel={handleFirmaPointerUp}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={handleClearFirma}
                        className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Limpiar firma
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleCloseFirmaModal}
                          className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleGuardarFirma}
                          className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          Guardar firma
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {renderEventosRecientes()}
          </>
        )}
      </div>

      {Boolean(error) && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-red-100 bg-red-600 text-white">
              <h3 className="text-lg font-semibold">No se pudo completar la acción</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">{error}</p>
              <p className="text-xs text-gray-500">Revisa la información solicitada y vuelve a intentarlo.</p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseErrorModal}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseSuccessModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con icono de éxito */}
            <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Registro exitoso</h3>
                  <p className="text-sm text-gray-600 mt-0.5">Visitante registrado correctamente</p>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="px-6 py-5 space-y-4">
              {/* Información del visitante */}
              {(visitanteInfoModal.nombre || visitanteInfoModal.apellido) && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm font-bold text-indigo-600">{`${visitanteInfoModal.nombre} ${visitanteInfoModal.apellido}`.trim() || '—'}</span>
                    </div>
                    {lastBitacoraId && (
                      <div className="flex items-center gap-2 pt-1">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs text-gray-600">ID de bitácora: <span className="font-mono font-semibold">{lastBitacoraId}</span></span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mensaje de confirmación */}
              <div className="text-center">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {success || 'El visitante ha sido registrado correctamente en la bitácora del puesto.'}
                </p>
              </div>

              {/* Indicador visual */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Registro confirmado</span>
              </div>
            </div>

            {/* Footer con botón */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseSuccessModal}
                className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RegistrarVisitante;

